import React, { useEffect, useState } from 'react';
import { Copy, Check, RefreshCw } from 'lucide-react';
import Button from './Button';
import { countdownTo } from '../utils/format';
import '../../../styles/parentPortal.css';

export default function SupervisionCodeCard({ code, expiresAt, generating, onRegenerate }) {
  const [copied, setCopied] = useState(false);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!expiresAt) return undefined;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [expiresAt]);

  useEffect(() => {
    if (!copied) return undefined;
    const id = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(id);
  }, [copied]);

  // Re-read every tick.
  void tick;
  const countdown = countdownTo(expiresAt);
  const expiredText = countdown
    ? countdown.expired
      ? 'Expired'
      : `Expires in ${countdown.mm}:${String(countdown.ss).padStart(2, '0')}`
    : '';

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
    } catch {
      // fall through silently
    }
  };

  return (
    <div className="pp-card" style={{ padding: 'var(--space-5)' }}>
      <div
        style={{
          fontSize: 'var(--text-label-size)',
          fontWeight: 'var(--text-label-weight)',
          color: 'var(--text-tertiary)',
          textTransform: 'uppercase',
          letterSpacing: 'var(--text-label-tracking)',
          marginBottom: 'var(--space-4)',
        }}
      >
        Your supervision code
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-3)',
          background: 'var(--bg-tertiary)',
          padding: 'var(--space-4) var(--space-5)',
          borderRadius: 'var(--radius-panel)',
          marginBottom: 'var(--space-4)',
        }}
      >
        <span
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 30,
            fontWeight: 700,
            letterSpacing: '0.2em',
            color: 'var(--text-primary)',
            flex: 1,
            userSelect: 'all',
          }}
        >
          {code}
        </span>
        <button
          className="pp-icon-btn"
          onClick={handleCopy}
          aria-label={copied ? 'Copied' : 'Copy code'}
          style={{
            background: 'transparent',
            border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--radius-control)',
            padding: 'var(--space-2)',
            color: copied ? 'var(--success)' : 'var(--text-secondary)',
            cursor: 'pointer',
            display: 'grid',
            placeItems: 'center',
          }}
        >
          {copied ? <Check size={16} strokeWidth={1.5} /> : <Copy size={16} strokeWidth={1.5} />}
        </button>
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 'var(--space-3)',
        }}
      >
        <p
          style={{
            margin: 0,
            fontSize: 'var(--text-body-size)',
            color: 'var(--text-secondary)',
            lineHeight: 1.5,
            maxWidth: 360,
          }}
        >
          Share this code with a parent, tutor, or mentor. They'll enter it on their account to link to you.
        </p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
          {expiredText && (
            <span
              style={{
                fontSize: 12,
                color: countdown?.expired ? 'var(--danger)' : 'var(--text-tertiary)',
                fontWeight: 600,
              }}
            >
              {expiredText}
            </span>
          )}
          <Button variant="secondary" size="sm" onClick={onRegenerate} loading={generating}>
            <RefreshCw size={14} strokeWidth={1.5} />
            New code
          </Button>
        </div>
      </div>
    </div>
  );
}
